import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { produce } from 'immer'
import { ROUTES } from '../../../../constants'
import { IPaginatedData, WithId } from 'models/shared.models'
import { IInvoice } from 'models/invoices.models'
import { selectActiveWorkspace } from 'lib/helpers/selectors'
import useDialog from 'hooks/Shared/useDialog'
import { useAppSelector } from 'store/hooks'
import { IApiError } from 'services/Shared/shared.models'
import { getCfdiList } from 'services/Invoices/invoices.services'
import { ICFDIFinderContainerProps } from './CFDIFinderContainer'

interface IUseCFDIFinderState {
  search: string
}

export interface IUseCFDIFinderContainer extends IUseCFDIFinderState {
  cfdisLoading: boolean
  getCFDIsQuery: UseQueryResult<IPaginatedData<WithId<IInvoice>>, IApiError>
  handleCancel: () => void
  updateCFDIFinderState: (update: (draft: IUseCFDIFinderState) => void) => void
}

type TUseCFDICOntainerProps = Pick<ICFDIFinderContainerProps, 'onCancel'>

const useCFDIFinderContainer = ({
  onCancel,
}: TUseCFDICOntainerProps): IUseCFDIFinderContainer => {
  const navigate = useNavigate()

  const dialog = useDialog()

  const activeWorkspace = useAppSelector(selectActiveWorkspace)

  const [CFDIFinderState, setCFDIFinderState] = useState<IUseCFDIFinderState>({
    search: '',
  })

  const { search } = CFDIFinderState

  const updateCFDIFinderState = useCallback(
    (update: (draft: IUseCFDIFinderState) => void) => {
      setCFDIFinderState(currentState => produce(currentState, update))
    },
    []
  )

  const getCFDIsQuery = useQuery<IPaginatedData<WithId<IInvoice>>, IApiError>(
    ['getCfdis', activeWorkspace, search],
    () =>
      getCfdiList({
        activeWorkspace,
        page: 0,
        pageSize: 50,
        filters: {
          search,
        },
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      onError: () => {
        dialog.danger({
          subtitle: 'Hubo un error al obtener la información de los CFDIs.',
          primaryButtonText: 'Reintentar',
          secondaryButtonText: 'Cerrar',
          onPrimaryClick: getCFDIsQuery.refetch,
        })
      },
    }
  )

  const { isFetching, isLoading } = getCFDIsQuery

  const cfdisLoading = isFetching || isLoading

  const handleCancel = () => {
    if (onCancel) onCancel()
    else {
      navigate(ROUTES.HOME, { replace: true })
    }
  }

  return {
    ...CFDIFinderState,
    cfdisLoading,
    getCFDIsQuery,
    handleCancel,
    updateCFDIFinderState,
  }
}

export default useCFDIFinderContainer
